import React, {useEffect, useState} from 'react';
import Grid from '@material-ui/core/Grid'
import InputField from '../../atoms/input-field'
import RegularButton from '../../atoms/regular-button';
import {
  CustomAlert, RegularButtonOutline, TextAreaField,
} from '../../atoms'
import {
  QUERY_ACTIONS, QUERY_KEYS,
} from '../../../config/constants'
import {trim} from 'lodash';
import JobOfferWizardStyled from "./styled";
import i18n from "../../../assets/i18n";
import {ModalSimpleSmall} from "../../molecules";
import {useLocation, useParams} from "react-router";
import {useMutation} from "@tanstack/react-query";
import {patchJobOffer, postJobOffer} from "../../../api";
import {useHistory} from "react-router-dom";
import routes from "../../../config/routes";
import {manageQueriesWithPrefix} from "../../../api/mutations/mutationCallbacks";
import moment from "moment/moment";
import {useJobOffer} from "../../../api/queries/useJobOffers";
import SingleDate from "../../molecules/single-date-picker";
import BackButton from "../../atoms/back-button";

const JobOfferWizard = () => {
  const location = useLocation();
  const history = useHistory();
  const {id} = useParams();
  const mode = location.pathname.split('/').pop();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alert, setAlert] = useState({ message: '', severity: '' });

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [, setEvaluationProfile] = useState("");
  const [locationValue, setLocationValue] = useState("");
  const [endDate, setEndDate] = useState(null);

  const [isOperationInProgress, setIsOperationInProgress] = useState(false);

  useEffect(() => {
    if (alert.message) {
      setShowAlert(true);
      const timer = setTimeout(() => {
        setShowAlert(false);
        setAlert({ message: '', severity: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  const { data: jobOffer, isLoading: isLoadingJobOffer} = useJobOffer(id);

  useEffect(() => {
    if (mode === 'edit') {
      if (!isLoadingJobOffer && !jobOffer) {
        history.push(routes.job_offers);
      } else if (jobOffer) {
        setName(jobOffer.name);
        setLocationValue(jobOffer.location);
        setDescription(jobOffer.description);
        setEvaluationProfile(jobOffer.evaluation_profile);
        setEndDate(moment(jobOffer.end_date));
      }
    } else {
      setName("");
      setLocationValue("");
      setDescription("");
      setEvaluationProfile("");
      setEndDate(moment().add(7, 'days'));
    }
  }, [mode, id, isLoadingJobOffer, jobOffer]);

  const handleOnClickSave = () => {
    setIsModalOpen(true);
  }

  const handleCreateJobOffer = (data) => {
    // Se devuelve la petición para que mutateAsync espere a la respuesta (spinner del modal y catch)
    return postJobOffer(data).then(() => {
      manageQueriesWithPrefix([QUERY_KEYS.JOB_OFFERS, `options:${QUERY_KEYS.JOB_OFFERS}`], QUERY_ACTIONS.REFETCH);
      setAlert({ message: i18n.job_offers.wizard.successCreateJobOffer, severity: 'success' });
      setTimeout(() => {
        setIsModalOpen(false);
        history.push(routes.job_offers);
      }, 3000);
    });
  }

  const handleUpdateJobOffer = ({id, data}) => {
    // Se devuelve la petición para que mutateAsync espere a la respuesta (spinner del modal y catch)
    return patchJobOffer(id, data).then(() => {
      manageQueriesWithPrefix([QUERY_KEYS.JOB_OFFERS, `options:${QUERY_KEYS.JOB_OFFERS}`, `${QUERY_KEYS.JOB_OFFER_ROW}:${id}`], QUERY_ACTIONS.REFETCH);
      setAlert({ message: i18n.job_offers.wizard.successUpdateJobOffer, severity: 'success' });
      setTimeout(() => {
        setIsModalOpen(false);
        history.push(routes.job_offers);
      }, 3000);
    });
  }

  const mutationCreateJobOffer = useMutation(handleCreateJobOffer);
  const mutationUpdateJobOffer = useMutation(handleUpdateJobOffer);

  const handleSave = async (e) => {
    e.preventDefault();

    if(isOperationInProgress) {
      return;
    }

    setIsOperationInProgress(true);

    try {
      const data = {
        name: trim(name),
        description: trim(description),
        location: trim(locationValue),
        recruitment_source: "manual",
        end_at: endDate.format('YYYY-MM-DD'),
      };

      if (mode === 'create') {
        await mutationCreateJobOffer.mutateAsync(data);
      } else {
        await mutationUpdateJobOffer.mutateAsync({id, data});
        setIsOperationInProgress(false);
      }
    } catch (error) {
      if (mode === 'create') {
        setAlert({ message: i18n.job_offers.wizard.errorCreateJobOffer, severity: 'error' });
      } else {
        setAlert({ message: i18n.job_offers.wizard.errorUpdateJobOffer, severity: 'error' });
        console.error('Error durante la mutación:', error);
      }
      setIsOperationInProgress(false);
    }
  }

  // Fecha de fin: a partir de mañana, como hacía el isOutsideRange del calendario anterior
  const minEndDate = moment().add(1, 'day').startOf('day').toDate();

  return (
    <JobOfferWizardStyled>
      <BackButton title={i18n.job_offer_applications.backToJobOffers} onClick={() => history.push(routes.job_offers)} />

      <div className="headerSection">
        <h2>{mode === 'create' ? i18n.job_offers.wizard.title_create : i18n.job_offers.wizard.title_edit}</h2>
        <p className="description">{i18n.job_offers.wizard.description}</p>
      </div>

      {/* Formulario en una tarjeta blanca y en dos columnas, como el de crear proceso de GPP */}
      <div className="form-card">
        <div className="form-grid">
          <div className="form-field">
            <label>{i18n.job_offers.wizard.name}</label>
            <InputField
              value={name}
              placeholder={i18n.job_offers.wizard.name_placeholder}
              onChange={(e) => {
                setName(e.target.value)
              }}
            />
          </div>
          <div className="form-field">
            <label>{i18n.job_offers.wizard.end_at}</label>
            <SingleDate
              id="endDate_id"
              date={endDate}
              onDateChange={date => setEndDate(date)}
              minDate={minEndDate}
              placeholder={i18n.job_offers.wizard.end_at_placeholder}
            />
          </div>
          <div className="form-field">
            <label>{i18n.job_offers.wizard.location}</label>
            <InputField
              value={locationValue}
              placeholder={i18n.job_offers.wizard.location_placeholder}
              onChange={(e) => {
                setLocationValue(e.target.value)
              }}
            />
          </div>
          <div className="form-field">
            <label>{i18n.job_offers.wizard.description_value}</label>
            <TextAreaField
              value={description}
              placeholder={i18n.job_offers.wizard.description_value_placeholder}
              handleOnChange={(value) => {
                setDescription(value)
              }}
            />
          </div>
        </div>

        <div className="form-actions">
          <RegularButtonOutline title={i18n.ia_evaluation.wizard.cancel} onClick={() => {history.push(routes.job_offers)}}/>
          <RegularButton title={i18n.job_offers.wizard.save} onClick={handleOnClickSave}/>
        </div>
      </div>
      <ModalSimpleSmall
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        buttonTitle={i18n.job_offers.wizard.save_confirmation_button.replace('{mode}', i18n.job_offers.wizard[`${mode}_mode`])}
        handleButtonClick={handleSave}
        textContent={<>
          <Grid container alignItems="center">
            <Grid item xs={12} sm={12} style={{display: "flex", flexDirection: "column", gap: 15}}>
              <h4
                style={{fontWeight: "bold"}}>{i18n.job_offers.wizard.save_confirmation_title.replace('{mode}', i18n.job_offers.wizard[`${mode}_mode`])}</h4>
              {showAlert && <CustomAlert severity={alert.severity} message={alert.message}/>}
            </Grid>
          </Grid>
        </>
        }
      />
    </JobOfferWizardStyled>
  );
};

export default JobOfferWizard;
